// AccountHistoryTimeline.tsx
import React from 'react';
import { Timeline, Typography, Tag, Empty, Space, Card } from 'antd';
import {
  ClockCircleOutlined,
  SwapOutlined,
  FileTextOutlined,
  UserOutlined
} from '@ant-design/icons';
import moment from 'moment';

const { Text } = Typography;

interface AccountHistoryEntry {
  timestamp: string;
  requestId: string;
  requestType: 'LOAD' | 'WITHDRAW';
  requestNumber: string;
  changeType: 'ACCOUNT_ID' | 'ACCOUNT_NAME' | 'BOTH';
  oldAccountId?: string;
  newAccountId?: string;
  oldAccountName?: string;
  newAccountName?: string;
  changedBy: string;
}

interface AccountHistoryTimelineProps {
  accountHistory: AccountHistoryEntry[];
}

export const AccountHistoryTimeline: React.FC<AccountHistoryTimelineProps> = ({ accountHistory }) => {
  if (!accountHistory || accountHistory.length === 0) {
    return (
      <Empty
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        description="No hay cambios de cuenta registrados"
        style={{ padding: '40px 0' }}
      />
    );
  }

  // Sort by timestamp (newest first)
  const sortedHistory = [...accountHistory].sort((a, b) =>
    new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
  );

  const getChangeTypeColor = (changeType: string) => {
    switch (changeType) {
      case 'ACCOUNT_ID':
        return 'blue';
      case 'ACCOUNT_NAME':
        return 'green';
      case 'BOTH':
        return 'purple';
      default:
        return 'default';
    }
  };

  const getRequestTypeColor = (requestType: string) => {
    return requestType === 'LOAD' ? 'cyan' : 'orange';
  };

  const renderChangeDetails = (entry: AccountHistoryEntry) => {
    const changes: React.ReactNode[] = [];

    if (entry.changeType === 'ACCOUNT_ID' || entry.changeType === 'BOTH') {
      changes.push(
        <div key="accountId" style={{ marginBottom: '8px' }}>
          <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginBottom: '4px' }}>
            Account ID
          </Text>
          <Space size={4}>
            <Tag color="red" style={{ margin: 0 }}>
              {entry.oldAccountId || 'null'}
            </Tag>
            <SwapOutlined style={{ fontSize: '12px', color: '#999' }} />
            <Tag color="green" style={{ margin: 0 }}>
              {entry.newAccountId || 'null'}
            </Tag>
          </Space>
        </div>
      );
    }

    if (entry.changeType === 'ACCOUNT_NAME' || entry.changeType === 'BOTH') {
      changes.push(
        <div key="accountName" style={{ marginBottom: '8px' }}>
          <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginBottom: '4px' }}>
            Account Name
          </Text>
          <Space size={4}>
            <Tag color="red" style={{ margin: 0 }}>
              {entry.oldAccountName || 'null'}
            </Tag>
            <SwapOutlined style={{ fontSize: '12px', color: '#999' }} />
            <Tag color="green" style={{ margin: 0 }}>
              {entry.newAccountName || 'null'}
            </Tag>
          </Space>
        </div>
      );
    }

    return changes;
  };

  const timelineItems = sortedHistory.map((entry, index) => ({
    key: index,
    dot: <ClockCircleOutlined style={{ fontSize: '16px' }} />,
    children: (
      <Card size="small" style={{ marginBottom: '16px', borderRadius: '8px' }}>
        <Space direction="vertical" style={{ width: '100%' }} size={12}>
          {/* Header with timestamp and request info */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Space size={8}>
              <Tag icon={<FileTextOutlined />} color={getRequestTypeColor(entry.requestType)}>
                {entry.requestNumber}
              </Tag>
              <Tag color={getChangeTypeColor(entry.changeType)}>
                {entry.changeType.replace('_', ' ')}
              </Tag>
            </Space>
            <Text type="secondary" style={{ fontSize: '12px' }}>
              <ClockCircleOutlined /> {moment(entry.timestamp).format('DD/MM/YYYY HH:mm')}
            </Text>
          </div>

          {/* Change details */}
          {renderChangeDetails(entry)}

          {/* Changed by */}
          <div style={{ borderTop: '1px solid #f0f0f0', paddingTop: '8px' }}>
            <Text type="secondary" style={{ fontSize: '11px' }}>
              <UserOutlined /> Admin: {entry.changedBy}
            </Text>
          </div>
        </Space>
      </Card>
    )
  }));

  return (
    <div style={{ padding: '16px' }}>
      <Timeline
        mode="left"
        items={timelineItems}
      />
    </div>
  );
};
